import { Button, SystemIcon, Typography } from '@volvo/vcdk-react';
import copy from 'copy-to-clipboard';
import React, { useRef, useState } from 'react';

import { languageKey } from '@/config/EnviromentConfig';
import useSessionStorage from '@/hooks/useLocalStorage';

export type TEvent = {
  registeredAt: Date;
  message: string;
};

type TStatusColorOption = keyof typeof statusColors;

type TStatusColorOptions = {
  [key in TStatusColorOption]?: boolean;
};

const statusColors = {
  loading: '#ffa000',
  success: '#1a6c31',
  failure: '#bf2012',
  neutral: 'black',
} as const;

const getStatusColor = (options: TStatusColorOptions) => {
  let color: string = statusColors.neutral;
  for (const [colorKey, isActive] of Object.entries(options)) {
    if (!isActive) {
      continue;
    }
    color = statusColors[colorKey as TStatusColorOption];
  }
  return color;
};

interface IRSWDLDebugger
  extends Omit<IRSWDLDebuggerButtonProps, 'onClick'>, Omit<IRSWDLDebuggerPortalProps, 'isOpen' | 'onClose'> {}

export const RSWDLDebugger: React.FC<IRSWDLDebugger> = ({
  isPolling,
  isConnected,
  detectedNativeEnv,
  hasError,
  requests,
  responses,
  logs,
}) => {
  const showDebugPortal = useSessionStorage('rswdl-debug', false);
  return (
    <>
      <RSWDLDebuggerButton
        isPolling={isPolling}
        isConnected={isConnected}
        onClick={() => showDebugPortal.setTo(true)}
      />
      <RSWDLDebuggerPortal
        isPolling={isPolling}
        isConnected={isConnected}
        detectedNativeEnv={detectedNativeEnv}
        hasError={hasError}
        requests={requests}
        responses={responses}
        logs={logs}
        isOpen={showDebugPortal.value}
        languageReceived={localStorage.getItem(languageKey) ?? 'n/a'}
        onClose={() => showDebugPortal.setTo(false)}
      />
    </>
  );
};

interface IRSWDLDebuggerButtonProps {
  isPolling: boolean;
  isConnected: boolean;
  onClick: () => void;
}

const RSWDLDebuggerButton = ({ isPolling, isConnected, onClick }: IRSWDLDebuggerButtonProps) => {
  const statusColor = getStatusColor({
    loading: isPolling,
    success: isConnected,
    failure: !isPolling && !isConnected,
  });
  return (
    <button
      style={{
        backgroundColor: statusColor,
        opacity: 0.4,
        position: 'fixed',
        bottom: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10,
      }}
      onClick={onClick}
    >
      RSWDL
    </button>
  );
};

interface IRSWDLDebuggerPortalProps extends IRSWDLStatusesProps {
  requests: TEvent[];
  responses: TEvent[];
  logs: TEvent[];
  isOpen: boolean;
  onClose: () => void;
}

const RSWDLDebuggerPortal = ({
  isPolling,
  isConnected,
  detectedNativeEnv,
  hasError,
  requests,
  responses,
  logs,
  isOpen,
  onClose,
}: IRSWDLDebuggerPortalProps) => {
  const [height, setHeight] = useState(400);
  const elementRef = useRef<HTMLDivElement>(null);

  const rsTouchDownHandler = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!elementRef.current) {
      return;
    }
    const y = e.touches[0].clientY;
    const elementHeight = window.getComputedStyle(elementRef.current).height;
    const initialHeight = parseInt(elementHeight, 10);

    const touchMoveHandler = (touchEvent: TouchEvent) => {
      const dy = y - touchEvent.touches[0].clientY;
      const newHeight = initialHeight + dy;
      setHeight(newHeight);
    };

    const touchUpHandler = () => {
      document.removeEventListener('touchend', touchUpHandler);
      document.removeEventListener('touchmove', touchMoveHandler);
    };
    document.addEventListener('touchmove', touchMoveHandler);
    document.addEventListener('touchend', touchUpHandler);
  };
  const rsMouseDownHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!elementRef.current) {
      return;
    }
    const y = e.clientY;
    const elementHeight = window.getComputedStyle(elementRef.current).height;
    const initialHeight = parseInt(elementHeight, 10);

    const mouseMoveHandler = (mouseEvent: MouseEvent) => {
      mouseEvent.preventDefault();
      mouseEvent.stopPropagation();
      const dy = y - mouseEvent.clientY;
      const newHeight = initialHeight + dy;
      setHeight(newHeight);
    };

    const mouseUpHandler = () => {
      document.removeEventListener('mouseup', mouseUpHandler);
      document.removeEventListener('mousemove', mouseMoveHandler);
    };

    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  };
  if (!isOpen) {
    return null;
  }
  return (
    <div
      ref={elementRef}
      style={{
        border: '1px solid black',
        borderRadius: '1rem 1rem 0 0',
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: `${height}px`,
        backgroundColor: 'rgba(255,255,255,.7)',
        zIndex: 10000000000,
        padding: '1rem',
        backdropFilter: 'blur(8px)',
        boxSizing: 'content-box',
        overflowY: 'hidden',
        maxHeight: '80%',
        minHeight: '100px',
      }}
    >
      <div style={{ position: 'relative' }}>
        <Typography variant="body" style={{ paddingBottom: '1rem' }}>
          RSWDL Debugger
        </Typography>
        <div
          className="resizer"
          onMouseDown={rsMouseDownHandler}
          onTouchStart={rsTouchDownHandler}
          style={{
            cursor: 'ns-resize',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            top: 0,
          }}
        >
          <SystemIcon icon="hamburger-menu" />
        </div>
        <Button iconEnd="close" onClick={onClose} style={{ position: 'absolute', right: 0, top: 0 }}>
          Close
        </Button>
      </div>
      <div
        style={{
          display: 'flex',
          gap: '2rem',
          flexWrap: 'wrap',
          height: '100%',
          overflow: 'auto',
          paddingBottom: '3rem',
        }}
      >
        <RSWDLStatuses
          isPolling={isPolling}
          isConnected={isConnected}
          detectedNativeEnv={detectedNativeEnv}
          hasError={hasError}
          languageReceived={localStorage.getItem(languageKey) ?? 'n/a'}
        />
        <LogWindow title="Requests" data={requests} emptyListMessage="No requests made..." />
        <LogWindow title="Responses" data={responses} emptyListMessage="No responses received..." />
        <LogWindow title="Logs" data={logs} emptyListMessage="No logs received..." />
      </div>
    </div>
  );
};

type IRSWDLStatusesProps = {
  detectedNativeEnv: boolean;
  hasError: TEvent | undefined;
  isConnected: boolean;
  isPolling: boolean;
  languageReceived: string;
};

const RSWDLStatuses = ({
  isPolling,
  isConnected,
  detectedNativeEnv,
  hasError,
  languageReceived,
}: IRSWDLStatusesProps) => {
  return (
    <ul
      style={{
        display: 'flex',
        flex: '1 1 0px',
        flexDirection: 'column',
        gap: '.4rem',
        height: '100%',
      }}
    >
      <li>
        <Typography>Handshake status: </Typography>
        <Typography style={{ color: getStatusColor({ success: isConnected, failure: !isConnected }) }}>
          {isConnected ? 'Connected' : 'Not connected'}
        </Typography>
      </li>
      <li>
        <Typography>Polling: </Typography>
        <Typography style={{ color: getStatusColor({ success: isPolling, failure: !isPolling }) }}>
          {isPolling ? 'Yes' : 'No'}
        </Typography>
      </li>
      <li>
        <Typography>Detected native environment: </Typography>
        <Typography
          style={{
            color: getStatusColor({ success: detectedNativeEnv, failure: !detectedNativeEnv }),
          }}
        >
          {detectedNativeEnv ? 'Yes' : 'No'}
        </Typography>
      </li>
      <li>
        <Typography>Received language: </Typography>
        <Typography
          style={{
            color: getStatusColor({
              success: languageReceived != null,
              failure: languageReceived == null,
            }),
          }}
        >
          {languageReceived != null ? languageReceived : 'n/a'}
        </Typography>
      </li>
      <li>
        <Typography>Has error: </Typography>
        <Typography style={{ color: getStatusColor({ success: !hasError, failure: !!hasError }) }}>
          {hasError ? 'Yes' : 'No'}
        </Typography>
      </li>
      <LogWindow title="Latest error" data={hasError ? [hasError] : []} emptyListMessage="No errors registered..." />
    </ul>
  );
};

interface ILogWindowProps {
  title: string;
  data: TEvent[];
  emptyListMessage: string;
}

const LogWindow = ({ title, data, emptyListMessage }: ILogWindowProps) => {
  return (
    <div
      style={{
        flex: '3 1 0px',
        height: '100%',
        border: '1px solid black',
        borderRadius: '.5rem',
        overflowX: 'hidden',
        overflowY: 'auto',
        padding: '0 .5rem',
        position: 'relative',
        minWidth: '320px',
        backgroundColor: 'white',
      }}
    >
      <Typography
        variant="body"
        style={{
          textDecoration: 'underline',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          padding: '.5rem',
        }}
      >
        {title}
      </Typography>
      {!data.length ? (
        <Typography style={{ minHeight: 'calc(100% - 94px)' }}>{emptyListMessage}</Typography>
      ) : (
        <ul
          style={{
            padding: '.5rem',
            marginLeft: '1rem',
            listStyle: 'circle',
            minHeight: 'calc(100% - 64px)',
          }}
        >
          {data.map((value) => (
            <li
              key={value.registeredAt.toString()}
              style={{
                listStyle: 'circle',
                paddingBottom: '.5rem',
              }}
            >
              <Typography variant="caption1" style={{ textDecoration: 'underline' }}>
                {(value.registeredAt.toISOString() ?? '').toString()}
              </Typography>
              <Typography variant="body" style={{ wordBreak: 'break-all' }}>
                {value.message}
              </Typography>
            </li>
          ))}
        </ul>
      )}
      <Button
        variant="secondary"
        style={{
          position: 'sticky',
          bottom: '.2rem',
          left: 'calc(100% - .5rem)',
        }}
        disabled={!data.length}
        onClick={() => copy(JSON.stringify(data))}
      >
        Copy
      </Button>
    </div>
  );
};
